<template>
    <div class="animated fadeIn">
        <LoadingWidget
            v-if="initStatus != 1"
            :status="initStatus"
            @init="init"
        />
        <b-card v-else>
            <h5 class="mb-0">
                <i class="fa fa-fw fa-star mr-2" />Konser Penjualan
            </h5>
            <hr />
            <b-row>
                <b-col md="3">
                    <b-form-group label="Nama Konser">
                        <b-form-input
                            v-model="filter.name"
                            maxlength="100"
                            placeholder="Cari nama konser..."
                            @keydown.enter="getItems"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="Tanggal Konser">
                        <div class="d-flex align-items-center">
                            <b-form-input v-model="filter.date" type="date" />
                        </div>
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="Cabang/Kantor">
                        <multiselect
                            v-model="filter.office"
                            label="name"
                            track-by="id"
                            :internal-search="true"
                            deselect-label=""
                            select-label=""
                            placeholder="Semua cabang"
                            :options="optOffices"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="Status">
                        <b-form-select
                            id="filter-status"
                            v-model="filter.status"
                            :options="optStatus"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Aksi">
                        <b-button
                            variant="primary"
                            class="mr-2 float-left"
                            @click="getItems()"
                        >
                            <i class="fa fa-search mr-2" />Cari
                        </b-button>
                        <b-button
                            class="ml-2"
                            variant="danger"
                            @click="resetFilter"
                        >
                            Reset
                        </b-button>
                        <b-button
                            v-if="canManageConcert"
                            variant="success"
                            class="float-right"
                            @click="openCreateModal"
                        >
                            <i class="fa fa-plus mr-2" />Tambah
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-table
                bordered
                hover
                show-empty
                no-local-sorting
                :busy="isBusy"
                :items="items"
                :fields="fields"
                @sort-changed="onSortChanged"
                responsive
                class="mb-3"
            >
                <template v-slot:empty>
                    Tidak ada data konser penjualan.
                </template>
                <template v-slot:cell(submission_at)="row">
                    {{ $helper.parseDate(row.item.submission_at) }},
                    {{ $helper.parseHourMinute(row.item.submission_at) }}
                </template>
                <template v-slot:cell(date)="row">
                    <span>
                        {{ $helper.parseDate(row.item.date) }}
                    </span>
                </template>
                <template v-slot:cell(office_name)="row">
                    {{ row.item.office_name || "-" }}
                </template>
                <template v-slot:cell(require_down_payment)="row">
                    <b-badge :variant="row.item.require_down_payment !== false ? 'success' : 'secondary'">
                        {{ row.item.require_down_payment !== false ? "DP" : "No DP" }}
                    </b-badge>
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="getStatusBadge(row.item.status)[0]">{{
                        getStatusBadge(row.item.status)[1]
                    }}</b-badge>
                </template>
                <template v-slot:cell(history)="row">
                    <div>
                        <span class="small">
                            Dibuat oleh {{ row.item.created_by || "-" }}
                        </span>
                        <span class="d-block small text-muted">
                            {{ $helper.parseDate(row.item.created_at) }},
                            {{ $helper.parseHourMinute(row.item.created_at) }}
                        </span>
                    </div>

                    <div
                        v-if="row.item.status === KONSER_STATUS.DIBATALKAN"
                        class="mt-2"
                    >
                        <span class="small text-danger">
                            Dibatalkan oleh {{ row.item.deleted_by || "-" }}
                        </span>
                        <span class="d-block small text-muted">
                            {{ $helper.parseDate(row.item.deleted_at) }},
                            {{ $helper.parseHourMinute(row.item.deleted_at) }}
                        </span>
                    </div>
                </template>
                <template v-slot:cell(action)="row">
                    <div
                        class="d-flex justify-content-center align-items-center"
                    >
                        <router-link
                            :to="
                                '/konser-penjualan/konser/' +
                                    row.item.id +
                                    '/submissions'
                            "
                            class="pr-2"
                        >
                            <b-button
                                v-if="row.item.status !== KONSER_STATUS.DIBATALKAN"
                                size="sm"
                                variant="primary"
                                v-b-tooltip.hover.top
                                title="Daftar Peserta"
                            >
                                <i class="fa fa-fw fa-list" />
                            </b-button>
                        </router-link>
                        <b-button
                            v-if="row.item.status === KONSER_STATUS.AKTIF && canManageConcert"
                            size="sm"
                            variant="danger"
                            class="mr-2"
                            v-b-tooltip.hover.top
                            title="Batalkan"
                            @click="prepareDelete(row.item)"
                        >
                            <i class="fa fa-fw fa-times" />
                        </b-button>
                        <b-button
                            size="sm"
                            variant="info"
                            class="text-white"
                            v-b-tooltip.hover.top
                            title="Download Laporan"
                            @click="prepareDownload(row.item)"
                        >
                            <i class="fa fa-fw fa-download" />
                        </b-button>
                    </div>
                </template>
            </b-table>
            <PageSizer
                :totalRows="totalRows"
                :page="currentPage"
                :options="options"
                @limit-change="onLimitChange"
                @page-change="onPageChange"
            />
        </b-card>

        <ModalKonserPenjualan
            ref="modalKonser"
            :optOffices="optOffices"
            @refresh="getItems(currentPage)"
        />

        <!-- Modal Download Laporan -->
        <b-modal
            id="m-download"
            :title="downloadModalTitle"
            centered
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            hide-header-close
            ref="downloadModal"
        >
            <template #modal-title>
                {{ downloadModalTitle }}
                <button
                    type="button"
                    class="close"
                    aria-label="Close"
                    :disabled="downloadStatus == 0"
                    @click="$refs.downloadModal.hide()"
                    style="position: absolute; top: 0.5rem; right: 1rem;"
                >
                    <b-spinner small v-if="downloadStatus == 0" />
                    <span v-else aria-hidden="true">&times;</span>
                </button>
            </template>
            <div v-if="downloadStatus == 0">
                Menyiapkan laporan, mohon menunggu...
            </div>
            <div v-else-if="downloadStatus == -1">
                Gagal menyiapkan laporan. Silahkan coba lagi.
            </div>
            <div v-else>
                Laporan sudah siap. Tekan tombol dibawah ini untuk download
                laporan Excel.
                <div class="mt-2">
                    <a :href="downloadPath" download>
                        <b-button variant="primary">
                            <i class="fa fa-download mr-2" />Download Excel
                            Laporan
                        </b-button>
                    </a>
                </div>
            </div>
        </b-modal>

        <!-- Modal Peserta Konser -->
        <b-modal
            id="m-submissions"
            :title="submissionsModalTitle"
            centered
            no-close-on-backdrop
            no-close-on-esc
            size="lg"
            hide-footer
        >
            <b-table
                bordered
                hover
                show-empty
                :busy="isSubmissionsBusy"
                :items="submissions"
                :fields="submissionsFields"
                responsive
                class="mb-2"
            >
                <template v-slot:empty>
                    Tidak ada peserta.
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="row.item.status === 1 ? 'success' : 'warning'">
                        {{ row.item.status === 1 ? "Terdaftar" : "Antrian" }}
                    </b-badge>
                </template>
                <template v-slot:cell(submitted_at)="row">
                    {{ $helper.parseDate(row.item.submitted_at) }},
                    {{ $helper.parseHourMinute(row.item.submitted_at) }}
                </template>
            </b-table>
            <PageSizer
                :totalRows="submissionsTotalRows"
                :page="submissionsPage"
                :options="options"
                @limit-change="onSubmissionsLimitChange"
                @page-change="onSubmissionsPageChange"
            />
        </b-modal>

        <!-- Modal Batalkan Konser -->
        <b-modal
            id="m-delete"
            title="Batalkan Konser Penjualan"
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
        >
            <p class="mb-1">
                Apakah Anda yakin ingin membatalkan konser penjualan ini?
            </p>
            <p class="font-weight-bold">{{ activeItem.name }}</p>
            <div class="mt-4 text-right">
                <b-button
                    variant="danger"
                    :disabled="isSubmitting"
                    @click="confirmDelete"
                >
                    {{ isSubmitting ? "Membatalkan..." : "Batalkan" }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import ModalKonserPenjualan from "./ModalKonserPenjualan.vue";
import EnumPermissions from "@/shared/enum-permissions";

const KONSER_STATUS = {
    DIBATALKAN: 0,
    AKTIF: 1,
    SELESAI: 2
};

export default {
    components: {
        ModalKonserPenjualan
    },
    data() {
        return {
            KONSER_STATUS,
            initStatus: 0,
            isBusy: true,
            isSubmitting: false,
            show: 10,

            filter: {
                name: "",
                date: "",
                office: null,
                status: -1
            },

            options: [
                { value: 10, text: "10" },
                { value: 20, text: "20" },
                { value: 30, text: "30" },
                { value: 40, text: "40" },
                { value: 50, text: "50" }
            ],

            fields: [
                { key: "name", label: "Nama Konser", sortable: false },
                {
                    key: "submission_at",
                    label: "Tanggal Pendaftaran",
                    sortable: false
                },
                { key: "date", label: "Tanggal Konser", sortable: false },
                { key: "office_name", label: "Cabang/Kantor", sortable: false },
                {
                    key: "max_slot",
                    label: "Slot per Pedagang",
                    sortable: false
                },
                {
                    key: "max_quota",
                    label: "Jumlah Kuota",
                    sortable: false
                },
                {
                    key: "attending_count",
                    label: "Maks. Orang Hadir",
                    sortable: false
                },
                {
                    key: "require_down_payment",
                    label: "DP",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                {
                    key: "status",
                    label: "Status",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                {
                    key: "history",
                    label: "Riwayat",
                    sortable: false
                },
                { key: "action", label: "Aksi" }
            ],

            items: [],
            activeItem: {
                id: null,
                name: ""
            },
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,

            optOffices: [],
            optStatus: [
                { value: -1, text: "Semua status" },
                { value: KONSER_STATUS.AKTIF, text: "Aktif" },
                { value: KONSER_STATUS.SELESAI, text: "Selesai" },
                { value: KONSER_STATUS.DIBATALKAN, text: "Dibatalkan" }
            ],

            downloadStatus: 1,
            downloadPath: "",
            downloadModalTitle: "",

            activeSubmissionsConcert: null,
            submissionsModalTitle: "",
            submissions: [],
            submissionsPage: 1,
            submissionsPerPage: 10,
            submissionsTotalRows: 0,
            isSubmissionsBusy: false,
            submissionsFields: [
                { key: "pax", label: "Jumlah Orang", sortable: false },
                { key: "slot", label: "Slot", sortable: false },
                { key: "status", label: "Status", sortable: false },
                { key: "submitted_at", label: "Waktu Daftar", sortable: false }
            ]
        };
    },

    computed: {
        canManageConcert() {
            return (
                this.$helper.checkRoleNumber(
                    EnumPermissions.CONCERT_SALES_ADMIN.toString(),
                    localStorage.getItem("permissions")
                ) || (localStorage.getItem("roleName") && localStorage.getItem("roleName").includes("Superadmin"))
            );
        }
    },

    methods: {
        init() {
            this.initStatus = 0;

            let getOffices = this.getOffices();
            let getItems = this.getItems();

            Promise.all([getItems, getOffices])
                .then(() => {
                    this.initStatus = 1;
                })
                .catch(() => {
                    this.initStatus = -1;
                });
        },

        getOffices() {
            return this.$axios.get("master/offices").then(res => {
                this.optOffices = res.data.data;
            });
        },

        openCreateModal() {
            this.$refs.modalKonser.openModal();
        },

        prepareDownload(item) {
            this.downloadModalTitle =
                "Laporan Peserta - " + item.name + " - " + item.office_name;
            this.$bvModal.show("m-download");
            this.downloadStatus = 0;

            this.$axios
                .post(
                    `concerts/${item.id}/submissions/excel`,
                    {},
                    { timeout: 600000 }
                )
                .then(res => {
                    this.downloadStatus = 1;

                    let path = res.data.data.path;
                    this.downloadPath = path;
                })
                .catch(error => {
                    this.downloadStatus = -1;
                    this.$helper.parseError(this, error);
                });
        },

        prepareDelete(item) {
            this.activeItem = item;
            this.$bvModal.show("m-delete");
        },

        confirmDelete() {
            let isConfirmed = confirm(
                "Apakah anda yakin ingin membatalkan konser penjualan ini? Perubahan ini tidak dapat dikembalikan!"
            );
            if (!isConfirmed) return;

            this.isSubmitting = true;

            this.$axios
                .delete("concerts/" + this.activeItem.id)
                .then(() => {
                    this.$helper.toastSuccess(
                        this,
                        "Konser penjualan berhasil dibatalkan!"
                    );
                    this.$bvModal.hide("m-delete");
                    this.getItems();
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false));
        },

        getItems(page = 1) {
            this.isBusy = true;
            this.items = [];

            if (isNaN(page)) {
                this.currentPage = 1;
            } else {
                this.currentPage = page;
            }

            let param = this.$helper.getBasePagedQuery(
                this.currentPage,
                this.perPage
            );

            if (!this.$helper.isBlank(this.filter.name)) {
                param += "&name=" + this.filter.name;
            }

            if (!this.$helper.isBlank(this.filter.date)) {
                param += "&date=" + this.filter.date;
            }

            if (this.filter.office != null) {
                param += "&office_id=" + this.filter.office.id;
            }

            if (this.filter.status != null && this.filter.status !== -1) {
                param += "&status=" + this.filter.status;
            }

            if (!this.$helper.isBlank(this.sortBy)) {
                param += "&sortBy=" + this.sortBy;
                param += "&sortDesc=" + this.sortDesc;
            }

            return this.$axios
                .get("concerts" + param)
                .then(res => {
                    this.items = res.data.data.items;
                    this.totalRows = res.data.data.total;
                    sessionStorage.setItem(
                        "konserPenjualanFilter",
                        JSON.stringify(this.filter)
                    );
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },

        resetFilter() {
            this.filter = {
                name: "",
                date: "",
                office: null,
                status: -1
            };

            this.getItems();
        },

        getStatusBadge(status) {
            switch (status) {
                case KONSER_STATUS.DIBATALKAN:
                    return ["danger", "DIBATALKAN"];
                case KONSER_STATUS.AKTIF:
                    return ["warning", "AKTIF"];
                case KONSER_STATUS.SELESAI:
                    return ["success", "SELESAI"];
                default:
                    return ["light", "-"];
            }
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getItems(this.currentPage);
        },

        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getItems(this.currentPage);
        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.getItems();
        },

        prepareViewSubmissions(item) {
            this.activeSubmissionsConcert = item;
            this.submissionsModalTitle = "Peserta - " + item.name;
            this.submissionsPage = 1;
            this.submissionsPerPage = 10;
            this.$bvModal.show("m-submissions");
            this.getSubmissions(1);
        },

        getSubmissions(page = 1) {
            if (!this.activeSubmissionsConcert) return;
            this.isSubmissionsBusy = true;
            this.submissions = [];
            this.submissionsPage = page;

            let param = this.$helper.getBasePagedQuery(page, this.submissionsPerPage);

            this.$axios
                .get(`concerts/${this.activeSubmissionsConcert.id}/submissions${param}`)
                .then(res => {
                    this.submissions = res.data.data.items;
                    this.submissionsTotalRows = res.data.data.total;
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmissionsBusy = false));
        },

        onSubmissionsLimitChange(newPerPage) {
            this.submissionsPerPage = newPerPage;
            this.getSubmissions(this.submissionsPage);
        },

        onSubmissionsPageChange(newPage) {
            this.getSubmissions(newPage);
        }
    },

    created() {
        if (
            !this.$helper.isBlank(
                sessionStorage.getItem("konserPenjualanFilter")
            )
        ) {
            this.filter = JSON.parse(
                sessionStorage.getItem("konserPenjualanFilter")
            );
        }
        this.init();
    }
};
</script>
